/**
 * Gantt chart component built with D3.js v7.
 *
 * Renders task bars spanning startDate → endDate, milestone diamonds,
 * a "Today" marker line, percent-complete fill, animated entrance, and
 * row hover highlights. Capped at 30 items.
 */

import { useRef, useEffect, useCallback } from "react";
import * as d3 from "d3";
import type { GanttChartConfig } from "../../types/index.js";
import { positionTooltip, formatDate, formatDateShort, useResizeObserver } from "./chartUtils.js";
import { useRenderLoopGuard } from "../../hooks/useRenderLoopGuard.js";

interface GanttChartViewProps {
	config: GanttChartConfig;
	isModal?: boolean;
}

const ROW_HEIGHT = 36;
const ROW_PADDING = 8;
const BAR_HEIGHT = 20;
const LABEL_WIDTH = 140;
const MARGINS = { top: 20, right: 16, bottom: 40 };

export default function GanttChartView({ config, isModal: _isModal = false }: GanttChartViewProps) {
	useRenderLoopGuard("GanttChartView");
	const svgRef = useRef<SVGSVGElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);
	const { width: containerWidth } = useResizeObserver(containerRef);

	const items = (config.items ?? []).slice(0, 30);

	const renderChart = useCallback(() => {
		if (!svgRef.current || items.length === 0 || containerWidth === 0) return;

		const svg = d3.select(svgRef.current);
		svg.selectAll("*").remove();

		const width = containerWidth;
		const chartW = width - LABEL_WIDTH - MARGINS.right;
		const height = items.length * ROW_HEIGHT + MARGINS.top + MARGINS.bottom;

		svg
		.attr("width", width)
		.attr("height", height)           // intrinsic size for SVG export
		.style("height", `${height}px`)   // CSS override: beats initial height:0 on SVG element
		.attr("role", "img");

		// Parse dates — fallback gracefully for invalid strings
		const parseDate = (s: string) => {
			const d = new Date(s);
			return isNaN(d.getTime()) ? new Date() : d;
		};

		const startDates = items.map((it) => parseDate(it.startDate));
		const endDates = items.map((it) => parseDate(it.endDate));
		const today = new Date();

		const minDate = d3.min([...startDates, today]) ?? today;
		const maxDate = d3.max([...endDates, today]) ?? today;

		// Add a small padding to the date domain
		const domainMin = new Date(minDate.getTime() - 86400000 * 2);
		const domainMax = new Date(maxDate.getTime() + 86400000 * 4);

		const xScale = d3.scaleTime().domain([domainMin, domainMax]).range([0, chartW]);

		// Clip path so bars don't overflow chart area
		svg.append("defs")
			.append("clipPath")
			.attr("id", "gantt-clip")
			.append("rect")
			.attr("x", 0)
			.attr("y", 0)
			.attr("width", chartW)
			.attr("height", height);

		// Labels group (left)
		const labelsG = svg
			.append("g")
			.attr("transform", `translate(0,${MARGINS.top})`);

		// Chart area group (right)
		const chartG = svg
			.append("g")
			.attr("transform", `translate(${LABEL_WIDTH},${MARGINS.top})`)
			.attr("clip-path", "url(#gantt-clip)");

		// Row backgrounds
		items.forEach((_, i) => {
			chartG
				.append("rect")
				.attr("class", `gantt-row-bg gantt-row-${i}`)
				.attr("x", 0)
				.attr("y", i * ROW_HEIGHT)
				.attr("width", chartW)
				.attr("height", ROW_HEIGHT)
				.attr("fill", i % 2 === 0 ? "#f9fafb" : "#fff")
				.attr("rx", 0)
				.style("cursor", "default")
				.on("mouseenter", function () {
					d3.select(this).attr("fill", "#eff6ff");
					// Also highlight sibling label row
					labelsG.select(`.gantt-label-bg-${i}`).attr("fill", "#eff6ff");
				})
				.on("mouseleave", function (_, j) {
					// i is in closure
					d3.select(this).attr("fill", i % 2 === 0 ? "#f9fafb" : "#fff");
					labelsG.select(`.gantt-label-bg-${i}`).attr("fill", "transparent");
				});
		});

		// Label backgrounds (for hover synchronisation)
		items.forEach((_, i) => {
			labelsG
				.append("rect")
				.attr("class", `gantt-label-bg-${i}`)
				.attr("x", 0)
				.attr("y", i * ROW_HEIGHT)
				.attr("width", LABEL_WIDTH)
				.attr("height", ROW_HEIGHT)
				.attr("fill", "transparent");
		});

		// Grid lines (vertical — date ticks)
		const xAxis = d3
			.axisBottom<Date>(xScale)
			.tickFormat((d) => formatDateShort(d as Date))
			.tickSizeInner(-(items.length * ROW_HEIGHT))
			.tickSizeOuter(0)
			.ticks(Math.max(3, Math.floor(chartW / 80)));

		chartG
			.append("g")
			.attr("transform", `translate(0,${items.length * ROW_HEIGHT})`)
			.call(xAxis)
			.selectAll("text")
			.attr("font-family", "var(--font-body)")
			.attr("font-size", "11px")
			.attr("fill", "#6b7280")
			.attr("dy", "1.2em");

		chartG.selectAll(".tick line").attr("stroke", "#e5e7eb");
		chartG.select(".domain").attr("stroke", "#e5e7eb");

		// "Today" marker line
		const todayX = xScale(today);
		if (todayX >= 0 && todayX <= chartW) {
			chartG
				.append("line")
				.attr("x1", todayX)
				.attr("x2", todayX)
				.attr("y1", 0)
				.attr("y2", items.length * ROW_HEIGHT)
				.attr("stroke", "#F7A020")
				.attr("stroke-width", 2)
				.attr("stroke-dasharray", "4 3");

			chartG
				.append("text")
				.attr("x", todayX + 4)
				.attr("y", 12)
				.attr("font-family", "var(--font-body)")
				.attr("font-size", "9px")
				.attr("font-weight", "600")
				.attr("fill", "#F7A020")
				.text("Today");
		}

		// Task labels
		items.forEach((item, i) => {
			labelsG
				.append("text")
				.attr("x", LABEL_WIDTH - 12)
				.attr("y", i * ROW_HEIGHT + ROW_HEIGHT / 2 + ROW_PADDING / 2)
				.attr("text-anchor", "end")
				.attr("dominant-baseline", "middle")
				.attr("font-family", "var(--font-body)")
				.attr("font-size", "11px")
				.attr("fill", "#374151")
				.attr("class", "gantt-label-text")
				// Truncate long names with title
				.text(item.name.length > 18 ? item.name.slice(0, 17) + "…" : item.name)
				.append("title")
				.text(item.name);
		});

		// Bars and milestones
		items.forEach((item, i) => {
			const sy = i * ROW_HEIGHT + (ROW_HEIGHT - BAR_HEIGHT) / 2;
			const startX = xScale(parseDate(item.startDate));
			const endX = xScale(parseDate(item.endDate));
			const barW = Math.max(4, endX - startX);
			const colour = item.colour || "#1B6BC4";
			const pct = item.percentComplete ?? 0;

			if (item.isMilestone) {
				// Diamond marker at startDate
				const mx = xScale(parseDate(item.startDate));
				const my = i * ROW_HEIGHT + ROW_HEIGHT / 2;
				const dm = BAR_HEIGHT / 2 - 2;

				chartG
					.append("path")
					.attr("d", `M${mx},${my - dm} L${mx + dm},${my} L${mx},${my + dm} L${mx - dm},${my} Z`)
					.attr("fill", colour)
					.attr("stroke", "#fff")
					.attr("stroke-width", 1.5)
					.style("cursor", "pointer")
					.attr("opacity", 0)
					.on("mouseenter", function (event: MouseEvent) {
						if (tooltipRef.current) {
							tooltipRef.current.innerHTML = `<strong>${item.name}</strong><br/>Milestone: ${formatDate(item.startDate)}<br/>Status: ${item.status}`;
							tooltipRef.current.style.opacity = "1";
							positionTooltip(tooltipRef.current, event);
						}
						d3.select(this).attr("opacity", 0.7);
					})
					.on("mousemove", function (event: MouseEvent) {
						if (tooltipRef.current) positionTooltip(tooltipRef.current, event);
					})
					.on("mouseleave", function () {
						if (tooltipRef.current) tooltipRef.current.style.opacity = "0";
						d3.select(this).attr("opacity", 1);
					})
					.transition()
					.duration(500)
					.delay(i * 30)
					.ease(d3.easeBounceOut)
					.attr("opacity", 1);
			} else {
				// Background bar (full width)
				chartG
					.append("rect")
					.attr("x", startX)
					.attr("y", sy)
					.attr("height", BAR_HEIGHT)
					.attr("rx", 4)
					.attr("fill", colour)
					.attr("opacity", 0.2)
					// Animate from startX width=0
					.attr("width", 0)
					.on("mouseenter", function (event: MouseEvent) {
						if (tooltipRef.current) {
							tooltipRef.current.innerHTML = `<strong>${item.name}</strong><br/>${formatDate(item.startDate)} → ${formatDate(item.endDate)}<br/>Status: ${item.status}${pct > 0 ? `<br/>Progress: ${pct}%` : ""}`;
							tooltipRef.current.style.opacity = "1";
							positionTooltip(tooltipRef.current, event);
						}
					})
					.on("mousemove", function (event: MouseEvent) {
						if (tooltipRef.current) positionTooltip(tooltipRef.current, event);
					})
					.on("mouseleave", function () {
						if (tooltipRef.current) tooltipRef.current.style.opacity = "0";
					})
					.transition()
					.duration(600)
					.delay(i * 25)
					.ease(d3.easeCubicOut)
					.attr("width", barW);

				// Progress fill bar
				if (pct > 0) {
					chartG
						.append("rect")
						.attr("x", startX)
						.attr("y", sy)
						.attr("height", BAR_HEIGHT)
						.attr("rx", 4)
						.attr("fill", colour)
						.attr("pointer-events", "none")
						.attr("width", 0)
						.transition()
						.duration(600)
						.delay(i * 25)
						.ease(d3.easeCubicOut)
						.attr("width", Math.max(4, barW * (pct / 100)));
				}

				// Status text inside bar (if wide enough)
				if (barW > 50 && item.status) {
					chartG
						.append("text")
						.attr("x", startX + barW / 2)
						.attr("y", sy + BAR_HEIGHT / 2)
						.attr("text-anchor", "middle")
						.attr("dominant-baseline", "middle")
						.attr("font-family", "var(--font-body)")
						.attr("font-size", "9px")
						.attr("fill", colour)
						.attr("pointer-events", "none")
						.text(item.status);
				}
			}
		});
	}, [items, containerWidth]);

	useEffect(() => {
		renderChart();
	}, [renderChart]);

	if (!items || items.length === 0) {
		return (
			<div className="chart-empty-state" role="status">
				No data available
			</div>
		);
	}

	return (
		<div ref={containerRef} style={{ position: "relative", width: "100%", height: "100%", overflowX: "auto" }}>
			{/* height: 0 prevents the browser default 150px SVG height from creating
			     whitespace before D3 fires (especially on history load). D3's renderChart
			     sets .style("height") to override this once containerWidth resolves. */}
			<svg ref={svgRef} style={{ display: "block", minWidth: "400px", height: 0 }} />

			{/* Tooltip */}
			<div
				ref={tooltipRef}
				className="chart-tooltip"
				role="tooltip"
				aria-live="polite"
				style={{ opacity: 0, position: "fixed" }}
			/>
		</div>
	);
}
